import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, memo } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { DashboardLayoutRoute } from "./components/dashboard/DashboardLayoutRoute";
import Index from "./pages/Index";

// Lazy load non-landing pages for smaller initial bundle
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const CookiesPage = lazy(() => import("./pages/Cookies"));
const Kontakt = lazy(() => import("./pages/Kontakt"));

// Lazy load Dashboard Pages
const DashboardOverview = lazy(() => import("./pages/dashboard/DashboardOverview"));
const DashboardChat = lazy(() => import("./pages/dashboard/DashboardChat"));
const DashboardProjects = lazy(() => import("./pages/dashboard/DashboardProjects"));
const ProjectDetailPage = lazy(() => import("./pages/dashboard/ProjectDetailPage"));
const DashboardTasks = lazy(() => import("./pages/dashboard/DashboardTasks"));
const DashboardData = lazy(() => import("./pages/dashboard/DashboardData"));
const DashboardFinanzen = lazy(() => import("./pages/dashboard/DashboardFinanzen"));
const DashboardInsurance = lazy(() => import("./pages/dashboard/DashboardInsurance"));
const DashboardMarketing = lazy(() => import("./pages/dashboard/DashboardMarketing"));
const DashboardTeam = lazy(() => import("./pages/dashboard/DashboardTeam"));
const DashboardSystem = lazy(() => import("./pages/dashboard/DashboardSystem"));
const DashboardSettings = lazy(() => import("./pages/dashboard/DashboardSettings"));
const DashboardCalendar = lazy(() => import("./pages/dashboard/DashboardCalendar"));
const DashboardAIChat = lazy(() => import("./pages/dashboard/DashboardAIChat"));
const DashboardContacts = lazy(() => import("./pages/dashboard/DashboardContacts"));
const DashboardEmail = lazy(() => import("./pages/dashboard/DashboardEmail"));
const DashboardPersonal = lazy(() => import("./pages/dashboard/DashboardPersonal"));
const DashboardIntegrations = lazy(() => import("./pages/dashboard/DashboardIntegrations"));
const DashboardStorage = lazy(() => import("./pages/dashboard/DashboardStorage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - reduces refetching
      gcTime: 1000 * 60 * 30, // 30 minutes cache
      refetchOnWindowFocus: false, // Prevents unnecessary refetches
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1, // Only retry once on failure
    },
  },
});

// Minimal loading fallback for instant page switches
const PageLoader = memo(() => (
  <div className="flex items-center justify-center h-32">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
));
PageLoader.displayName = 'PageLoader';

// Full-screen loader for public pages
const FullPageLoader = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
));
FullPageLoader.displayName = 'FullPageLoader';

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
    <TooltipProvider delayDuration={300}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Suspense fallback={<FullPageLoader />}><Login /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<FullPageLoader />}><ResetPassword /></Suspense>} />
          <Route path="/impressum" element={<Suspense fallback={<FullPageLoader />}><Impressum /></Suspense>} />
          <Route path="/datenschutz" element={<Suspense fallback={<FullPageLoader />}><Datenschutz /></Suspense>} />
          <Route path="/agb" element={<Suspense fallback={<FullPageLoader />}><AGB /></Suspense>} />
          <Route path="/widerruf" element={<Suspense fallback={<FullPageLoader />}><Widerruf /></Suspense>} />
          <Route path="/cookies" element={<Suspense fallback={<FullPageLoader />}><CookiesPage /></Suspense>} />
          <Route path="/kontakt" element={<Suspense fallback={<FullPageLoader />}><Kontakt /></Suspense>} />
          <Route path="/startseite" element={<Suspense fallback={<FullPageLoader />}><LandingPage /></Suspense>} />
          
          {/* Dashboard Routes - Layout stays mounted across all child routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayoutRoute /></ProtectedRoute>}>
            <Route index element={<Suspense fallback={<PageLoader />}><DashboardOverview /></Suspense>} />
            <Route path="chat" element={<Suspense fallback={<PageLoader />}><DashboardChat /></Suspense>} />
            <Route path="projekte" element={<Suspense fallback={<PageLoader />}><DashboardProjects /></Suspense>} />
            <Route path="projekte/:projectId" element={<Suspense fallback={<PageLoader />}><ProjectDetailPage /></Suspense>} />
            <Route path="aufgaben" element={<Suspense fallback={<PageLoader />}><DashboardTasks /></Suspense>} />
            <Route path="daten" element={<Suspense fallback={<PageLoader />}><DashboardData /></Suspense>} />
            <Route path="finanzen" element={<Suspense fallback={<PageLoader />}><DashboardFinanzen /></Suspense>} />
            <Route path="versicherungen" element={<Suspense fallback={<PageLoader />}><DashboardInsurance /></Suspense>} />
            <Route path="marketing" element={<Suspense fallback={<PageLoader />}><DashboardMarketing /></Suspense>} />
            <Route path="kalender" element={<Suspense fallback={<PageLoader />}><DashboardCalendar /></Suspense>} />
            <Route path="ki-chat" element={<Suspense fallback={<PageLoader />}><DashboardAIChat /></Suspense>} />
            <Route path="kontakte" element={<Suspense fallback={<PageLoader />}><DashboardContacts /></Suspense>} />
            <Route path="email" element={<Suspense fallback={<PageLoader />}><DashboardEmail /></Suspense>} />
            <Route path="personal" element={<Suspense fallback={<PageLoader />}><DashboardPersonal /></Suspense>} />
            <Route path="team" element={<Suspense fallback={<PageLoader />}><DashboardTeam /></Suspense>} />
            <Route path="system" element={<Suspense fallback={<PageLoader />}><DashboardSystem /></Suspense>} />
            <Route path="integrationen" element={<Suspense fallback={<PageLoader />}><DashboardIntegrations /></Suspense>} />
            <Route path="storage" element={<Suspense fallback={<PageLoader />}><DashboardStorage /></Suspense>} />
            <Route path="einstellungen" element={<Suspense fallback={<PageLoader />}><DashboardSettings /></Suspense>} />
          </Route>
          
          {/* Admin Dashboard */}
          <Route path="/admin" element={<Suspense fallback={<FullPageLoader />}><AdminDashboard /></Suspense>} />

          {/* Catch-all */}
          <Route path="*" element={<Suspense fallback={<FullPageLoader />}><NotFound /></Suspense>} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
